import { useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import * as api from '@lendica/api';

/**
 * Custom hook for autosaving form data
 * @param {Object} formik - Formik instance
 * @param {Function} onSave - Optional callback after successful save
 * @param {Object} options - Configuration options
 * @param {number} options.delay - Delay in milliseconds before autosaving (default: 3000)
 * @param {Function} options.transformValues - Optional function to transform values before saving
 */
export const useAutosave = (formik, onSave, options = {}) => {
    const { delay = 3000, transformValues } = options;
    const lastSavedValuesRef = useRef(null);
    const autoSaveTimeoutRef = useRef(null);

    // Initialize lastSavedValuesRef
    useEffect(() => {
        if (!lastSavedValuesRef.current && formik) {
            lastSavedValuesRef.current = formik.initialValues;
        }
    }, [formik?.initialValues]);

    // Auto-save when form values change
    useEffect(() => {
        if (!formik) {
            // console.log('useAutosave: formik is not available');
            return;
        }

        // Clear any existing timeout
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        const isDirty = formik.dirty;
        const valuesChanged = !isEqual(formik.values, lastSavedValuesRef.current);

        // Check if form is dirty and values have changed (removed isValid check to allow partial saves)
        if (isDirty && valuesChanged) {
            // console.log('useAutosave: Setting up auto-save timeout');
            // Set a new timeout for auto-save
            autoSaveTimeoutRef.current = setTimeout(async () => {
                // console.log('Auto-saving form changes...');
                try {
                    // Validate before saving; skip save if there are any errors
                    const validationErrors =
                        typeof formik.validateForm === 'function'
                            ? await formik.validateForm()
                            : formik.errors || {};
                    if (validationErrors && Object.keys(validationErrors).length > 0) {
                        // console.log('Auto-save skipped due to validation errors');
                        return;
                    }

                    // Transform values if a transformer function is provided
                    const valuesToSave = transformValues
                        ? transformValues(formik.values)
                        : formik.values;

                    // console.log('Values to save:', valuesToSave);
                    await api.postApplication(valuesToSave);
                    lastSavedValuesRef.current = { ...formik.values };

                    // Call onSave callback if provided
                    if (onSave) {
                        onSave(formik.values);
                    }

                    // console.log('Auto-save successful');
                } catch (error) {
                    // console.error('Auto-save failed:', error);
                }
            }, delay);
        } else {
            // console.log('useAutosave: Skipping auto-save', {
            //     reason: !isDirty
            //         ? 'form not dirty'
            //         : !valuesChanged
            //         ? 'values unchanged'
            //         : 'unknown',
            // });
        }

        // Cleanup timeout on unmount
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [formik?.values, formik?.dirty, formik?.isValid, delay, transformValues, onSave]);

    return {
        isAutosaving: formik?.dirty || false,
    };
};
